import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { Textarea, TextareaInput } from "@repo/components/src/ui/textarea";
import { AlertCircleIcon } from "lucide-react-native";
import { ComponentProps } from "react";
import { Controller, FieldValues } from "react-hook-form";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputProps } from "../types";

export type FormInputTextProps<TFieldValues extends FieldValues> =
  FormInputProps<TFieldValues, string | undefined> & {
    size?: ComponentProps<typeof FormControl>["size"];
    keyboardType: ComponentProps<typeof TextareaInput>["keyboardType"];
    textContentType: ComponentProps<typeof TextareaInput>["textContentType"];
    autoCapitalize: ComponentProps<typeof TextareaInput>["autoCapitalize"];
    numberOfLines?: ComponentProps<typeof TextareaInput>["numberOfLines"];
    textareaClassName?: ComponentProps<typeof Textarea>["className"];
    textareaInputClassName?: ComponentProps<typeof TextareaInput>["className"];
  };

export default function FormInputTextArea<TFieldValues extends FieldValues>(
  props: FormInputTextProps<TFieldValues>,
) {
  const {
    control,
    name,
    placeholder,
    label,
    required,
    className,
    size,
    keyboardType,
    textContentType,
    autoCapitalize,
    numberOfLines,
    textareaClassName = "",
    textareaInputClassName = "",
    labelSize = "md",
  } = props;
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { onChange, value, ref, onBlur, disabled },
        fieldState: { error },
      }) => (
        <FormControl
          isInvalid={!!error}
          isRequired={required}
          isDisabled={disabled}
          className={className}
          size={size}
        >
          {label && (
            <FormControlLabel>
              <FormControlLabelText size={labelSize}>
                {label}
              </FormControlLabelText>
            </FormControlLabel>
          )}
          <Textarea
            className={textareaClassName}
            isDisabled={disabled}
            isInvalid={!!error}
            isRequired={required}
            size={size}
          >
            <TextareaInput
              ref={focusScrollRef(ref)}
              onBlur={onBlur}
              placeholder={placeholder}
              value={value || ""}
              onChangeText={onChange}
              keyboardType={keyboardType}
              textContentType={textContentType}
              autoCapitalize={autoCapitalize}
              numberOfLines={numberOfLines}
              className={textareaInputClassName}
            />
          </Textarea>
          <FormControlError>
            <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
            <FormControlErrorText>{error?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      )}
    />
  );
}
